// Settings > NameIt! Pro (for subscribers): plan and billing details. Cancelling
// lives here, behind a retention step, rather than on the Settings screen.
import { useState } from "react";
import { Crown, Loader2 } from "lucide-react";
import BottomSheet from "../BottomSheet";

const formatDate = (value) =>
  new Date(value).toLocaleDateString("en-NG", { dateStyle: "long" });

function Detail({ label, value, note }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5">
      <span className="text-sm text-brand-textSecondary">{label}</span>
      <span className="text-right">
        <span className="block text-sm font-semibold text-brand-text">{value}</span>
        {note && <span className="block text-xs text-brand-textSecondary">{note}</span>}
      </span>
    </div>
  );
}

export default function ProSheet({ isOpen, onClose, proType, proExpiry, cancelling, onCancel }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const monthly = proType === "monthly";
  const expiry = proExpiry ? new Date(proExpiry) : null;
  const daysLeft = expiry ? Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const close = () => {
    setConfirming(false);
    setError("");
    onClose();
  };

  const handleCancel = async () => {
    setError("");
    const failure = await onCancel();
    if (failure) setError(failure);
    else close();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={cancelling ? () => {} : close} title="NameIt! Pro" subtitle="Your subscription">
      <div className="flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3.5 mb-2">
        <Crown size={22} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
        <div>
          <p className="text-sm font-bold text-brand-text">Pro is active</p>
          <p className="text-xs text-brand-textSecondary">10 players, unlimited rooms and more</p>
        </div>
      </div>

      <div className="divide-y divide-brand-borderLight">
        <Detail label="Plan" value={monthly ? "Monthly subscription" : "1-month pass"} note={monthly ? "Renews every month" : "Doesn't renew"} />
        {expiry && (
          <Detail
            label={monthly ? "Next billing" : "Pro ends"}
            value={formatDate(expiry)}
            note={daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : undefined}
          />
        )}
      </div>

      {monthly && (
        <div className="mt-4 pt-4 border-t border-brand-borderLight">
          {confirming ? (
            <div className="space-y-3">
              <p className="text-sm text-brand-textSecondary">
                We'll process your request within 24 hours, and you'll keep Pro until the end of your billing period.
              </p>
              {error && (
                <p role="alert" className="rounded-xl bg-brand-accentMuted text-brand-accent text-sm font-medium px-4 py-3">
                  {error}
                </p>
              )}
              <button
                type="button"
                onClick={close}
                disabled={cancelling}
                className="w-full h-12 rounded-xl bg-brand-accent text-white font-semibold hover:bg-red-700 transition-colors"
              >
                Keep Pro
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 text-sm font-semibold text-brand-textSecondary hover:text-brand-text disabled:opacity-60"
              >
                {cancelling && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
                {cancelling ? "Cancelling…" : "Cancel subscription"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="w-full min-h-[44px] text-sm font-medium text-brand-textSecondary hover:text-brand-text"
            >
              Cancel subscription
            </button>
          )}
        </div>
      )}
    </BottomSheet>
  );
}
